import { useMutation, useQueryClient } from '@tanstack/react-query';
import ReferrerService from '@clickersphere/core/services/ReferrerService';
import { LeadQueryData, LEADS_QUERY_KEY } from '../lead/useLeads';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { REFERRERS_QUERY_KEY, ReferrerQueryData } from './useReferrers';

export function useDeleteReferrer() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();

  const {
    isPending: deleteReferrerIsPending,
    mutateAsync: deleteReferrerAsync,
  } = useMutation({
    mutationFn: async (id: string) => ReferrerService.remove(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(REFERRERS_QUERY_KEY),
      });
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(LEADS_QUERY_KEY),
      });

      const oldReferrers = queryClient.getQueryData<ReferrerQueryData[]>(
        buildQueryKey(REFERRERS_QUERY_KEY),
      );
      const oldLeadsQueries = queryClient.getQueriesData<unknown>({
        queryKey: buildQueryKey(LEADS_QUERY_KEY),
      });

      queryClient.setQueryData<ReferrerQueryData[]>(
        buildQueryKey(REFERRERS_QUERY_KEY),
        (old) => old?.filter((referrer) => referrer.id !== id),
      );

      const detachLead = (lead: LeadQueryData) =>
        lead.referrerId === id
          ? { ...lead, referrerId: null, referrerName: null }
          : lead;

      oldLeadsQueries.forEach(([queryKey, data]) => {
        if (Array.isArray(data)) {
          queryClient.setQueryData(
            queryKey,
            (data as LeadQueryData[]).map(detachLead),
          );
        } else if (data && typeof data === 'object') {
          queryClient.setQueryData(queryKey, detachLead(data as LeadQueryData));
        }
      });

      return { oldReferrers, oldLeadsQueries };
    },

    onError: async (_error, _id, context) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(REFERRERS_QUERY_KEY),
      });
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(LEADS_QUERY_KEY),
      });

      queryClient.setQueryData(
        buildQueryKey(REFERRERS_QUERY_KEY),
        context?.oldReferrers,
      );
      context?.oldLeadsQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
  });

  return {
    deleteReferrerAsync,
    deleteReferrerIsPending,
  };
}
